/**
 * The methods that run on figures the accounts cannot contain.
 *
 * One declarative table rather than a hand-written form each. Each method names its
 * fields, and the form, the labels, the hints and the units all come from here - so
 * adding a method is an entry, and a field added on the backend has exactly one place
 * to appear.
 *
 * WHY THESE ARE INPUTS AT ALL. None of the seven is blocked on arithmetic. Every one
 * is blocked on a figure a set of statutory accounts does not contain: a property
 * valuation, an actuarial reserve, a drug programme's peak sales. The methodology
 * choices that make these methods contentious - which cap rate, what probability of
 * approval, how to split a conglomerate - are stated by the person doing the
 * valuation rather than buried in code with nothing to check itself against.
 *
 * The `block` keys and field names mirror `app/valuation/v2/method_inputs.py` exactly,
 * because they are sent as-is and validated there. `methodSpecs.test.ts` pins that
 * they still match.
 */

/**
 * A rate is entered as a PERCENTAGE and stored as a fraction. A quantity is a count of
 * physical units - ounces, tonnes - kept apart from money so it is never shown as one.
 */
export type FieldKind = 'money' | 'rate' | 'multiple' | 'years' | 'quantity' | 'choice' | 'text';

export interface MethodField {
  key: string;
  label: string;
  kind: FieldKind;
  hint?: string;
  /** For a 'choice': the values the backend accepts, and what each is called - the FIRST
   *  being the backend's default, which is what the field shows until one is chosen. */
  options?: { value: string; label: string }[];
}

export interface MethodSpec {
  /** The key under `methodInputs`. Mirrors the backend field name. */
  block: string;
  /** The method's own id, as the result rows report it. */
  method: string;
  label: string;
  /** One line on what the method does and when it is the right one. */
  about: string;
  /** Single figures. */
  fields: MethodField[];
  /** A repeatable table, where the method has one. */
  list?: {
    key: string;
    /** Singular, for the "Add a …" button. */
    noun: string;
    columns: MethodField[];
  };
}

export const METHOD_SPECS: MethodSpec[] = [
  {
    block: 'nav',
    method: 'nav',
    label: 'Net asset value',
    about:
      'Assets valued one by one, less what is owed. For a property company, an ' +
      'investment company or a miner, where each asset has been valued on its own ' +
      'terms — a single total would throw away the only interesting part.',
    fields: [
      {
        key: 'liabilities',
        label: 'Total liabilities',
        kind: 'money',
        hint: 'Everything owed, including debt. Enter as a positive figure.',
      },
    ],
    list: {
      key: 'assets',
      noun: 'asset',
      columns: [
        { key: 'name', label: 'Asset', kind: 'text' },
        { key: 'value', label: 'Value', kind: 'money' },
      ],
    },
  },
  {
    block: 'commodityNav',
    method: 'commodity_nav',
    label: 'Commodity NAV (mines at a price deck)',
    about:
      'A commodity producer valued mine by mine: each one sold at a price deck over its ' +
      'life, less royalty, all-in sustaining cost, tax and the capital still to spend, ' +
      'discounted at a REAL rate. The prices are in today’s money, so the rate must be ' +
      'real too — a nominal cost of capital undervalues every long-life mine. Shown at ' +
      'prices higher and lower as well, because the long-term price is most of the answer.',
    fields: [
      { key: 'commodity', label: 'Commodity', kind: 'text', hint: 'Gold, copper, coal…' },
      { key: 'unit', label: 'Unit', kind: 'text', hint: 'What the price is quoted per: oz, lb, t.' },
      { key: 'spotPrice', label: 'Price today, per unit', kind: 'money' },
      {
        key: 'longTermPrice',
        label: 'Long-term price, per unit',
        kind: 'money',
        hint: 'In today’s money.',
      },
      {
        key: 'yearsToLongTermPrice',
        label: 'Years to reach it',
        kind: 'years',
        hint: 'A straight line from today’s price; 0 uses the long-term price from the start.',
      },
      {
        key: 'nearTermPrices',
        label: 'Near-term prices',
        kind: 'choice',
        options: [
          { value: 'deck', label: 'Straight line from today’s price' },
          { value: 'forward', label: 'Forward curve (cost of carry)' },
        ],
        hint: 'What prices the years before the long-term price. The forward curve is what the market contracts at today, not a forecast.',
      },
      {
        key: 'interestRate',
        label: 'Interest rate (forward curve)',
        kind: 'rate',
        hint: 'Nominal, to the delivery dates.',
      },
      {
        key: 'leaseRate',
        label: 'Lease rate (forward curve)',
        kind: 'rate',
        hint: 'What lending the metal earns. Gold’s is usually well under 1.',
      },
      {
        key: 'inflationRate',
        label: 'Inflation (forward curve)',
        kind: 'rate',
        hint: 'Forward prices are nominal; this puts them in today’s money beside the rest.',
      },
      {
        key: 'realDiscountRate',
        label: 'Real discount rate',
        kind: 'rate',
        hint: 'Around 5 for gold in a stable country, higher for country risk.',
      },
      {
        key: 'priceSensitivity',
        label: 'Price move to test (optional)',
        kind: 'rate',
        hint: 'Blank is 20 — the value is also shown at prices 20% lower and higher.',
      },
      {
        key: 'explorationValue',
        label: 'Exploration and resources',
        kind: 'money',
        hint: 'Resources outside the mine plans, valued separately.',
      },
      {
        key: 'corporateCosts',
        label: 'Corporate costs capitalised',
        kind: 'money',
        hint: 'Head office costs the mines do not carry. Positive; it reduces the total.',
      },
      {
        key: 'netDebt',
        label: 'Net debt (optional)',
        kind: 'money',
        hint: 'Left blank, this comes from the figures — the last year’s debt less cash.',
      },
    ],
    list: {
      key: 'mines',
      noun: 'mine',
      columns: [
        { key: 'name', label: 'Mine', kind: 'text' },
        { key: 'annualProduction', label: 'Production a year', kind: 'quantity' },
        { key: 'mineLife', label: 'Life', kind: 'years', hint: 'Years of production left.' },
        {
          key: 'yearsToProduction',
          label: 'Years to production',
          kind: 'years',
          hint: '0 for a mine producing today.',
        },
        { key: 'costPerUnit', label: 'All-in sustaining cost / unit', kind: 'money' },
        {
          key: 'developmentCapex',
          label: 'Capital to spend',
          kind: 'money',
          hint: 'Before production, spread evenly over the years to it.',
        },
        { key: 'royaltyRate', label: 'Royalty', kind: 'rate' },
        { key: 'taxRate', label: 'Tax', kind: 'rate' },
        { key: 'closureCost', label: 'Closure cost', kind: 'money' },
        { key: 'ownership', label: 'Share owned', kind: 'rate', hint: 'Blank is 100.' },
        {
          key: 'probability',
          label: 'Chance of production',
          kind: 'rate',
          hint: 'Blank is 100, for a producing mine. Weights the whole value, capital included.',
        },
      ],
    },
  },
  {
    block: 'commodityHoldings',
    method: 'commodity_holdings',
    label: 'Commodity holdings at market',
    about:
      'Physical commodity held as an asset — bullion in a vault, a trust’s metal. Gold ' +
      'earns nothing, so there is no cash flow to discount: it is worth the quantity at ' +
      'today’s price, less what it costs to sell, with anything already sold forward at ' +
      'its contract price. Shown at prices higher and lower as well.',
    fields: [
      {
        key: 'otherNetAssets',
        label: 'Other net assets',
        kind: 'money',
        hint: 'Cash and anything else held, less anything else owed. May be negative.',
      },
      { key: 'liabilities', label: 'Borrowings and other liabilities', kind: 'money' },
      {
        key: 'priceSensitivity',
        label: 'Price move to test (optional)',
        kind: 'rate',
        hint: 'Blank is 20.',
      },
    ],
    list: {
      key: 'holdings',
      noun: 'holding',
      columns: [
        { key: 'name', label: 'Holding', kind: 'text' },
        { key: 'quantity', label: 'Quantity', kind: 'quantity' },
        { key: 'price', label: 'Price today / unit', kind: 'money' },
        {
          key: 'hedgedQuantity',
          label: 'Sold forward',
          kind: 'quantity',
          hint: 'Part of the quantity, not on top of it.',
        },
        { key: 'hedgePrice', label: 'Contract price / unit', kind: 'money' },
        {
          key: 'costToSell',
          label: 'Cost to sell',
          kind: 'rate',
          hint: 'Refining, transport and the dealer’s spread.',
        },
      ],
    },
  },
  {
    block: 'sotp',
    method: 'sotp',
    label: 'Sum of the parts',
    about:
      'Each part of the business valued however suits it, then added up. Group debt ' +
      'comes off once at the end rather than being split between the parts.',
    fields: [
      { key: 'netDebt', label: 'Group net debt', kind: 'money' },
      {
        key: 'centralCosts',
        label: 'Central costs capitalised',
        kind: 'money',
        hint: 'Head office costs the parts do not carry. Positive; it reduces the total.',
      },
    ],
    list: {
      key: 'segments',
      noun: 'part',
      columns: [
        { key: 'name', label: 'Part', kind: 'text' },
        {
          key: 'enterpriseValue',
          label: 'Enterprise value',
          kind: 'money',
          hint: 'What this part is worth BEFORE group debt.',
        },
      ],
    },
  },
  {
    block: 'embeddedValue',
    method: 'embedded_value',
    label: 'Embedded value',
    about:
      'An insurer’s net worth plus the value of the policies it has already written. ' +
      'Both halves come out of a reserving exercise, so both are entered: deriving ' +
      'them from a profit and loss account would be inventing the answer.',
    fields: [
      {
        key: 'adjustedNetWorth',
        label: 'Adjusted net worth',
        kind: 'money',
        hint: 'Net assets on a market-consistent basis.',
      },
      {
        key: 'valueOfInForceBusiness',
        label: 'Value of in-force business',
        kind: 'money',
        hint: 'Present value of profits on policies already written.',
      },
    ],
  },
  {
    block: 'regulatedAsset',
    method: 'regulated_asset',
    label: 'Regulated asset value',
    about:
      'A regulated utility — water, electricity or gas networks — priced off the asset ' +
      'base its regulator sets returns on: Ofwat’s RCV, Ofgem’s RAV. The base at a premium ' +
      'or discount, less net debt. Read from the report where a table prints it.',
    fields: [
      {
        key: 'regulatedAssetValue',
        label: 'Regulated asset value',
        kind: 'money',
        hint: 'The regulator’s value of the asset base at the valuation date.',
      },
      {
        key: 'premium',
        label: 'Premium to it',
        kind: 'rate',
        hint: 'Blank is nil, the regulator’s own value. Negative for a discount.',
      },
      {
        key: 'netDebt',
        label: 'Net debt',
        kind: 'money',
        hint: 'Blank uses the accounts’.',
      },
      { key: 'source', label: 'Where it came from', kind: 'text' },
    ],
  },
  {
    block: 'arr',
    method: 'ev_arr',
    label: 'EV / ARR',
    about:
      'A multiple of annual recurring revenue, for a subscription business. ARR is ' +
      'not revenue: it counts the contracted, recurring part and excludes services ' +
      'and one-off licences, which is why it cannot be read off a profit and loss.',
    fields: [
      { key: 'arr', label: 'Annual recurring revenue', kind: 'money' },
      { key: 'multiple', label: 'EV / ARR multiple', kind: 'multiple' },
      {
        key: 'netDebt',
        label: 'Net debt (optional)',
        kind: 'money',
        hint: 'Left blank, this comes from the figures — the last year’s debt less cash.',
      },
    ],
  },
  {
    block: 'affo',
    method: 'affo',
    label: 'AFFO, capitalised',
    about:
      'Rental cash flow after the capex needed to keep the buildings earning it, ' +
      'capitalised at a yield. Unlike a cash flow model this is capitalised rather ' +
      'than discounted over a horizon.',
    fields: [
      { key: 'rentalCashFlow', label: 'Rental cash flow', kind: 'money' },
      {
        key: 'maintenanceCapex',
        label: 'Maintenance capex',
        kind: 'money',
        hint: 'What it costs to keep the portfolio earning, as against expanding it.',
      },
      { key: 'capRate', label: 'Capitalisation rate', kind: 'rate' },
      { key: 'netDebt', label: 'Net debt (optional)', kind: 'money' },
    ],
  },
  {
    block: 'aum',
    method: 'aum_based',
    label: 'AUM-based',
    about:
      'Assets under management, through the fee they earn, to a multiple of the ' +
      'profit on that fee. For an asset or wealth manager.',
    fields: [
      { key: 'aum', label: 'Assets under management', kind: 'money' },
      {
        key: 'feeRate',
        label: 'Fee rate',
        kind: 'rate',
        hint: 'What is earned on those assets. 0.75 means 75 basis points.',
      },
      { key: 'margin', label: 'Operating margin', kind: 'rate' },
      { key: 'earningsMultiple', label: 'Earnings multiple', kind: 'multiple' },
    ],
  },
  {
    block: 'ventureCapital',
    method: 'vc_method',
    label: 'Venture capital method',
    about:
      'What an exit is worth today at the return an investor needs. For a company ' +
      'with no profit and often no revenue, where there is nothing to multiply — ' +
      'so you decide what it sells for and discount that by the return the risk ' +
      'demands. A target return of 40% is not a cost of capital: it carries the ' +
      'chance the company is worth nothing.',
    fields: [
      {
        key: 'exitMetric',
        label: 'Metric at exit',
        kind: 'money',
        hint: 'The revenue or earnings in the exit year that the multiple applies to.',
      },
      {
        key: 'exitMetricName',
        label: 'Which metric',
        kind: 'text',
        hint: 'Revenue, ARR, EBITDA — whatever the multiple is quoted against.',
      },
      { key: 'exitMultiple', label: 'Exit multiple', kind: 'multiple' },
      { key: 'yearsToExit', label: 'Years to exit', kind: 'years' },
      {
        key: 'targetReturn',
        label: 'Target return a year',
        kind: 'rate',
        hint: 'What the investment has to earn. 40 means 40% a year.',
      },
      {
        key: 'investmentAmount',
        label: 'Investment now (optional)',
        kind: 'money',
        hint: 'Given, the basis also reports the pre-money value.',
      },
    ],
  },
  {
    block: 'rnpv',
    method: 'rnpv',
    label: 'Risk-adjusted NPV',
    about:
      'A pipeline, each programme weighted by its chance of reaching market. Costs ' +
      'still to spend are taken at FULL weight, not probability-weighted: they are ' +
      'spent whether or not the programme succeeds. Weighting them is the commonest ' +
      'way this method is made to flatter a pipeline.',
    fields: [
      {
        key: 'cash',
        label: 'Cash on hand',
        kind: 'money',
        hint: 'Much of an early-stage company’s value is this.',
      },
    ],
    list: {
      key: 'programmes',
      noun: 'programme',
      columns: [
        { key: 'name', label: 'Programme', kind: 'text' },
        { key: 'peakSales', label: 'Peak sales', kind: 'money' },
        { key: 'margin', label: 'Margin', kind: 'rate' },
        {
          key: 'probability',
          label: 'Probability',
          kind: 'rate',
          hint: 'The chance it ever reaches market. The number the whole method turns on.',
        },
        {
          key: 'remainingCost',
          label: 'Cost to complete',
          kind: 'money',
          hint: 'Taken at full weight — spent whether or not it succeeds.',
        },
        { key: 'yearsToPeak', label: 'Years to peak', kind: 'years' },
      ],
    },
  },
];

/**
 * A typed figure, from what someone typed.
 *
 * A rate is entered as a percentage and stored as a fraction, in one place, because a
 * field that accepts either "0.75" or "0.75%" is a field that will be read both ways —
 * and a cap rate wrong by a factor of a hundred values a portfolio at a hundredth of
 * its worth while looking like a modelling disagreement.
 */
export function toStored(kind: FieldKind, raw: string): number | string | null {
  if (kind === 'text') return raw;
  if (kind === 'choice') return raw === '' ? null : raw;
  const text = raw.trim();
  if (text === '') return null;
  const value = Number(text.replace(/[,%\s]/g, ''));
  if (Number.isNaN(value)) return null;
  if (kind === 'rate') return value / 100;
  if (kind === 'years') return Math.round(value);
  return value;
}

/** The inverse, for filling the form back in from what was saved. */
export function toTyped(kind: FieldKind, stored: unknown): string {
  if (stored === null || stored === undefined) return '';
  if (kind === 'text' || kind === 'choice') return String(stored);
  if (typeof stored !== 'number') return '';
  if (kind === 'rate') return String(Number((stored * 100).toPrecision(10)));
  return String(stored);
}

export const UNIT_SUFFIX: Record<FieldKind, string> = {
  money: '',
  rate: '%',
  multiple: '×',
  years: 'yrs',
  quantity: '',
  choice: '',
  text: '',
};
